import { Form, Space } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { ExternalAccess } from '../interfaces';
import { Store } from '../../../state/store.interfaces';
import Icon from '../../../common/components/Icon/Icon';
import { LrvForm } from '../../../common/components/LrvForm/LrvForm';
import { LrvText } from '../../../common/components/LrvText/LrvText';
import { useFormErrorFields } from '../../../hooks/useFormErrorFields';
import { LrvModal } from '../../../common/components/LrvModal/LrvModal';
import { LrvSwitch } from '../../../common/components/LrvSwitch/LrvSwitch';
import { LrvInputV2 } from '../../../common/components/LrvInputV2/LrvInputV2';
import { LrvPasswordV2 } from '../../../common/components/LrvPasswordV2/LrvPasswordV2';

import styles from './EditExternalAccess.module.scss';
import * as externalAccessSlice from './externalAccessSlice';

interface Props {
  theme?: 'dark' | 'light';
}

export const EditExternalAccess = (props: Props) => {
  const { theme } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [formExternalAccess] = Form.useForm();

  const { errorFields, extractAndSetErrorFields, resetErrorFields } = useFormErrorFields({ form: formExternalAccess });

  const [active, setActive] = useState<boolean | undefined>(false);
  const [apiName, setApiName] = useState('');
  const [apiUser, setApiUser] = useState('');
  const [apiPassword, setApiPassword] = useState('');
  const [apiUrl, setApiUrl] = useState('');

  const selectedCompany = useSelector((state: Store) => state.header.company);
  const { showEditModal, selectedExternalAccess } = useSelector((state: Store) => state.externalAccess);

  useEffect(() => {
    if (!showEditModal || !selectedExternalAccess) {
      return;
    }

    setActive(selectedExternalAccess.active);
    setApiName(selectedExternalAccess.apiConfig.name);
    setApiUser(selectedExternalAccess.apiConfig.user);
    setApiPassword(selectedExternalAccess.apiConfig.password);
    setApiUrl(selectedExternalAccess.apiConfig.url);

    formExternalAccess.setFieldsValue({
      apiName: selectedExternalAccess.apiConfig.name,
      apiUser: selectedExternalAccess.apiConfig.user,
      apiUrl: selectedExternalAccess.apiConfig.url,
      apiPassword: '',
    });
  }, [showEditModal, formExternalAccess, selectedExternalAccess, ]);

  const renderApiName = () => {
    const hasError = errorFields.includes('apiName');

    return (
      <Form.Item
        name='apiName'
        rules={[{ required: true, message: t('common.requiredField') }]}
      >
        <LrvInputV2
          label={t('integrations.apiConfig.name')}
          value={apiName}
          onChange={(e) => setApiName(e.target.value)}
          theme={theme}
          hasError={hasError}
        />
      </Form.Item>
    );
  };

  const renderApiUser = () => {
    const hasError = errorFields.includes('apiUser');

    return (
      <Form.Item
        name='apiUser'
        rules={[{ required: true, message: t('common.requiredField') }]}
      >
        <LrvInputV2
          label={t('integrations.apiConfig.user')}
          value={apiUser}
          onChange={(e) => setApiUser(e.target.value)}
          theme={theme}
          hasError={hasError}
        />
      </Form.Item>
    );
  };

  const renderApiPassword = () => {
    const hasError = errorFields.includes('apiPassword');

    return (
      <Form.Item
        name='apiPassword'
        rules={[{ required: true, message: t('common.requiredField') }]}
      >
        <LrvPasswordV2
          label={t('integrations.apiConfig.password')}
          value={apiPassword}
          onChange={(e) => setApiPassword(e.target.value)}
          theme={theme}
          hasError={hasError}
        />
      </Form.Item>
    );
  };

  const renderApiUrl = () => {
    const hasError = errorFields.includes('apiUrl');

    return (
      <Form.Item
        name='apiUrl'
        rules={[{ required: true, message: t('common.requiredField') }]}
      >
        <LrvInputV2
          label={t('integrations.apiConfig.url')}
          value={apiUrl}
          onChange={(e) => setApiUrl(e.target.value)}
          theme={theme}
          hasError={hasError}
        />
      </Form.Item>
    );
  };

  const onUpdateExternalAccess = () => {
    const body: ExternalAccess = {
      _id: selectedExternalAccess?._id,
      active,
      companyId: selectedCompany._id,
      apiConfig: {
        name: apiName,
        user: apiUser,
        password: apiPassword,
        url: apiUrl,
      },
    };

    dispatch(externalAccessSlice.updateExternalAccess({ body, onSuccess: onCloseModal }));
  };

  const onCloseModal = () => {
    setApiPassword('');
    setApiUrl('');
    setApiUser('');
    setApiName('');

    dispatch(externalAccessSlice.setShowEditModal(false));

    formExternalAccess.resetFields();
    resetErrorFields();
  };

  const isValidForm = () => {
    return apiUser && apiPassword && apiUrl && apiName;
  };

  return (
    <LrvModal
      theme='light'
      title={t('integrations.edit')}
      open={showEditModal}
      closeIcon={<Icon id='close_modal' name='close' />}
      destroyOnClose={true}
      okButtonProps={{ htmlType: 'submit', form: 'formExternalAccess', disabled: !isValidForm() }}
      onOk={onUpdateExternalAccess}
      okText={t('actions.edit')}
      onCancel={onCloseModal}
      className={styles.container}
    >
      <LrvForm
        id='formExternalAccess'
        form={formExternalAccess}
        name='formExternalAccess'
        layout='vertical'
        theme={theme}
        onFieldsChange={extractAndSetErrorFields}
      >
        <Form.Item className={styles.switch}>
          <Space className={styles.enableButton}>
            <LrvText
              theme='light'
              text={t('integrations.active')}
            />
            <LrvSwitch
              theme='light'
              checked={active}
              onChange={(value) => setActive(value)}
            />
          </Space>
        </Form.Item>
            
        <Form.Item>
          <LrvText theme={theme} text={t('integrations.apiConfig.title')} />
        </Form.Item>

        {renderApiName()}
        {renderApiUser()}
        {renderApiPassword()}
        {renderApiUrl()}
      </LrvForm>
    </LrvModal>
  );
};
